import { Guid } from "guid-typescript";
import { computed, EventEmitter, Injectable, signal, WritableSignal } from "@angular/core";
import { OpenDocInfoDto } from "../dto/open-doc-info-dto";
import { NotifyTargetDocMessage, SaveDocRequestMessage, UserPreferenceChangedMessage } from "../../../../commondata/messages/messages-dto";
import { MESSAGE, MODULE_TYPE } from "../../../../commondata/messages/msg-constants";
import { DocDto } from "../dto/doc-dto";
import { AuthenticationService } from "../core/authentication/authentication.service";
import { FirebaseService } from "../core/firebase/firebase.service";
import { Subscription } from "rxjs";
import { ApiService } from "../core/api/api.service";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { SelectedExtrasDto } from "../dto/selected-extras-dto";
import { SelectedFixedLinksDto } from "../dto/selected-fixed-links";
import { CustomLinkDto } from "../dto/custom-link-dto";
import { ModuleSpacesService } from "./module-spaces.service";
import { SnackBarService } from "../services/snackbar.service";
import { WordprocessorBackendService } from "../services/wordprocessor-backend.service";
import { LoaderService } from "../shared/loader/loader.service";
import { TtsService } from "../services/tts.service";
import { ExtraType } from "./types/extra-type";
import { UiConstants } from "../shared/ui-constants";
import { ShellEmittersService } from "../shell-emitters.service";
import { NetworkStatusLineService } from "../services/network-status-line.service";
import { MatDialog } from "@angular/material/dialog";
import { ShellStateService } from "../shell-state.service";
import { TLArea, TLAreaFunction, TLAreaType, TLFunctionType } from "../claire/area-function";
import { ClaireConfigService } from "../claire/claire-config.service";
import { DialogAdvancedFunctionComponent } from "./dialogs/dialog-advanced-function/dialog-advanced-function.component";

@Injectable({
  providedIn: "root",
})


export class WorkspaceService {
  private _docsLoaded: WritableSignal<boolean> = signal(false);
  public docsLoaded = computed(() => this._docsLoaded());

  private allDocs: DocDto[] = new Array<DocDto>();

  private _tags = signal<string[]>([]);
  public signalTags = this._tags.asReadonly();

  private _docsFiltered = signal<DocDto[]>([]);
  public readonly docsFiltered = this._docsFiltered.asReadonly();
  private _docs = signal<DocDto[]>([]);
  public readonly docs = this._docs.asReadonly();

  private _pdfs = signal<DocDto[]>([]);
  public readonly pdfs = this._pdfs.asReadonly();

  private _maps = signal<DocDto[]>([]);
  public readonly maps = this._maps.asReadonly();

  private _selectedExtra: WritableSignal<SelectedExtrasDto> = signal(new SelectedExtrasDto(null));
  private _selectedFixedLinks: WritableSignal<SelectedFixedLinksDto> = signal(new SelectedFixedLinksDto(null));
  private _selectedCustomLinks: WritableSignal<CustomLinkDto[]> = signal([]);
  private _openExtraType: WritableSignal<ExtraType> = signal(undefined);
  private _selectedKeyboard: WritableSignal<string> = signal('advanced');
  private _selectedAdvancedKeyboards: WritableSignal<string[]> = signal([]);
  private _setSelectedClaire: WritableSignal<any> = signal({});

  selectedExtra = computed(() => this._selectedExtra());
  selectedFixedLinks = computed(() => this._selectedFixedLinks());
  selectedCustomLinks = computed(() => this._selectedCustomLinks());
  openExtraType = computed(() => this._openExtraType());
  selectedKeyboardType = computed(() => this._selectedKeyboard());
  selectedAdvancedKeyboards = computed(() => this._selectedAdvancedKeyboards());

  public OpeningDocInfo: any = {};

  atLeastOneAdditionalToolSelected = computed(() => {
    let b =
      this.selectedFixedLinks().desmosCalculator ||
      this.selectedFixedLinks().desmosScientific || this.selectedFixedLinks().desmosFourFunction || this.selectedFixedLinks().desmosGeometry
    if (!b) {
      for (let i = 0; i < this.selectedCustomLinks().length; i++) {
        if (this.selectedCustomLinks()[i].selected) {
          b = true;
          break;
        }
      }
    }
    return b;
  });

  public loadedAllDocuments = new EventEmitter<boolean>();
  public canCloseDoc = new EventEmitter<string>();
  public openDocument = new EventEmitter<string>();

  isCapturingScreen: WritableSignal<boolean> = signal(false);
  screenLockedCapturing: WritableSignal<boolean> = signal(false);

  focusedSpace: number = 0;

  saveConfigTimer: any;
  public isInitialized: boolean = false;

  private getRecentDocsSubscription: Subscription | undefined;
  private getUserPreferencesSubscription: Subscription | undefined;
  private addSideBySideSubscription: Subscription | undefined;

  public noImageUrl: string = "";
  private PREF_KEY = "workspaceExtras";
  private wsConfig: any = {};

  constructor(
    private router: Router,
    private shellStateService: ShellStateService,
    private authenticationService: AuthenticationService,
    private firebaseService: FirebaseService,
    private apiService: ApiService,
    private translateService: TranslateService,
    private moduleSpacesService: ModuleSpacesService,
    private shellEmittersService: ShellEmittersService,
    private snackBarService: SnackBarService,
    private loaderService: LoaderService,
    private ttsService: TtsService,
    private wordProcessorBackendService: WordprocessorBackendService,
    private networkStatusLineService: NetworkStatusLineService,
    private claireConfigService: ClaireConfigService,
    private dialog: MatDialog
  ) {
    (window as any).wss = this;
    this.getUserPreferencesPREF_KEY().catch((err) => console.error('Error loading preferences:', err));

  }


  getUserPreferencesPREF_KEY(): Promise<void> {
    // Carica stato inziale dal server
    if (this.authenticationService.isAuthenticated()) {
      return this.getUserPreferencesXPREF();
    }
    return Promise.resolve();
  }

  private getUserPreferencesXPREF(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getUserPreferencesSubscription?.unsubscribe();
      this.getUserPreferencesSubscription = this.apiService.getUserPreferences(this.PREF_KEY).subscribe({
        next: (res: any) => {
          console.log("workspace config: ", res);
          if (res && res.ok && res.result) {
            this.wsConfig = res.result;
            if (!this.wsConfig) this.wsConfig = {};
            if (!this.wsConfig.selectedMathKeyboardType) this.wsConfig.selectedMathKeyboardType = 'advanced';
            if (!this.wsConfig.selectedMathKeyboards) this.wsConfig.selectedMathKeyboards = [...UiConstants.MATH_KEYBOARDS.ADVANCED];
            this.setSelectedExtra(res.result.selectedExtra ?? {});
            this.setSelectedFixedLinks(res.result.selectedFixedLinks ?? {});
            this.setSelectedCustomLinks(res.result.selectedCustomLinks ?? []);
            this.setSelectedMathKeyboardType(res.result.selectedMathKeyboardType ?? []);
            this.setSelectedAdvancedMathKeyboards(res.result.selectedMathKeyboards ?? []);
            this.claireConfigService.loadConfig(res.result.claireCfg);
            this.isInitialized = true;
            this.shellEmittersService.onMathPreferencesLoaded.emit({ keyboardType: this.wsConfig.selectedMathKeyboardType, keyboards: this.wsConfig.selectedMathKeyboards });
          }
          resolve();
        },
        error: (err: any) => {
          console.log(err);
          reject(err);
        }
      });
    });
  }
  delayedSaveConfig() {
    // Salvataggio ritardato: in caso di click multipli salva dopo un tempo di inattività
    if (!this.isInitialized) return;
    if (this.saveConfigTimer) {
      clearInterval(this.saveConfigTimer);
    }
    this.saveConfigTimer = setInterval(() => {
      clearInterval(this.saveConfigTimer);
      this.apiService.updateUserPreference(this.PREF_KEY, this.wsConfig).subscribe({
        next: (res: any) => {
          console.log("workspace config saved: ", res);
          //this.shellEmittersService.onTLRequestedConfig.emit();
        },
        error: (err: any) => {
          console.log(err);
        }
      });
    }, 1000);
    this.shellEmittersService.onMathPreferencesLoaded.emit({ keyboardType: this.wsConfig.selectedMathKeyboardType, keyboards: this.wsConfig.selectedMathKeyboards });
  }

  setOpenExtraType(openExtraType: ExtraType) {
    this._openExtraType.update((value) => openExtraType);
  }

  setSelectedExtra(selectedExtra: SelectedExtrasDto) {
    this._selectedExtra.update((value) => selectedExtra);
    if (this.isInitialized) {
      this.wsConfig = { ...this.wsConfig, ...{ selectedExtra: selectedExtra } };
    }
  }

  setSelectedClaire(isClaireEnabledByConfig: boolean, areas: { [key in TLAreaType]: TLArea }, functions: { [key in TLFunctionType]: TLAreaFunction }) {
    const claireCfg = {
      enabled: isClaireEnabledByConfig,
      areas: Object.keys(areas).filter(key => areas[key as keyof typeof areas].enabled),
      functions: Object.keys(functions).filter(key => functions[key as keyof typeof functions].enabled)
    };
    this.wsConfig = { ...this.wsConfig, ...{ claireCfg } };
    // Imposta configurazione
    this.claireConfigService.isClaireEnabledByConfig.set(isClaireEnabledByConfig);
    this.claireConfigService.areas.set(areas);
    this.claireConfigService.functions.set(functions);
    // Imposta Claire da configurazione in memoria
    const msg = {
      msgType: MESSAGE.MSGTYPE.TL_CONFIG,
      msgData: {
        tlAreas: {} as { [key in TLAreaType]?: boolean },
        tlFunctions: {} as { [key in TLFunctionType]?: boolean }
      }
    };
    for (const key of Object.keys(areas)) {
      (msg.msgData.tlAreas as { [key in TLAreaType]?: boolean })[key as TLAreaType] = areas[key as TLAreaType].enabled;
    }
    for (const key of Object.keys(functions)) {
      (msg.msgData.tlFunctions as { [key in TLFunctionType]?: boolean })[key as TLFunctionType] = functions[key as TLFunctionType].enabled;
    }
    // Invio del risultato a Claire
    const claire = document.getElementById('claire') as HTMLIFrameElement;
    if (claire) {
      claire?.contentWindow?.postMessage(msg, '*');
    }
  }

  setSelectedFixedLinks(selectedFixedLinks: SelectedFixedLinksDto) {
    if (Object.keys(selectedFixedLinks).length === 0) {
      selectedFixedLinks = {
        desmosCalculator: true,
        desmosFourFunction: true,
        desmosGeometry: true,
        desmosScientific: true
      };
    }
    this._selectedFixedLinks.update((value) => selectedFixedLinks);
    if (this.isInitialized) {
      this.wsConfig = { ...this.wsConfig, ...{ selectedFixedLinks: selectedFixedLinks } };
    }
  }

  setSelectedCustomLinks(selectedCustomLinks: CustomLinkDto[]) {
    this._selectedCustomLinks.update((value) => selectedCustomLinks);
    if (this.isInitialized) {
      this.wsConfig = { ...this.wsConfig, ...{ selectedCustomLinks: selectedCustomLinks } };
    }
  }

  setSelectedMathKeyboardType(keyboardType: 'advanced' | 'primary') {
    this._selectedKeyboard.set(keyboardType);
    if (this.isInitialized) {
      this.wsConfig.selectedMathKeyboardType = keyboardType;
    }
  }

  // Chiamata da GUI
  setSelectedAdvancedMathKeyboard(keyboardName: string, checked: boolean) {
    const keyboards = this._selectedAdvancedKeyboards();
    if (checked) {
      if (!keyboards.includes(keyboardName)) {
        keyboards.push(keyboardName);
      }
    } else {
      const idx = keyboards.indexOf(keyboardName);
      if (idx > -1) {
        keyboards.splice(idx, 1);
      }
    }
    if (this.isInitialized) {
      this.wsConfig.selectedMathKeyboards = keyboards;
    }
  }

  // Chiamata da constructor dopo il caricamento dei dati
  setSelectedAdvancedMathKeyboards(keyboardNames: string[]) {
    this._selectedAdvancedKeyboards.set(keyboardNames);
  }

  setSignalTags(tags: string[]) {
    this._tags.set(tags);
  }


  dispatchWorkspaceConfiguration(
    extra: OpenDocInfoDto | null,
    docs: Array<OpenDocInfoDto | null>,
    focusId: string | null
  ) {
    // Invio dello stato agli iframe e all'eventuale extra come NOTIFY_TARGET_DOC ad ognuno
    if (!docs || docs.length <= 0) return;
    const nDocs = docs.length;
    if (nDocs === 1) {
      this.notifyTargetDoc(docs[0], null);
    } else if (nDocs === 2) {
      this.notifyTargetDoc(docs[0], docs[1]);
      this.notifyTargetDoc(docs[1], docs[0]);
    }
    // TODO: notifica extra: se extra è remoto ok, se fa parte di shell conosce già la configurazione del workspace
  }

  private notifyTargetDoc(
    targetDoc: OpenDocInfoDto | null,
    sideDoc: OpenDocInfoDto | null
  ) {
    if (targetDoc) {
      if (!sideDoc) {
        sideDoc = {
          id: "", name: "", type: "", guid: "", isDirty: false, isShared: false, saveStatus: 0, isInShowMode: false, isInViewMode: false, isNewDoc: false, isBusy: false,
          hasChanged: false, isReadOnly: false, isLocked: false, startText: '', googleId: ''
        };
      }
      const iframe = this.findIFrameByGuid(
        targetDoc.type + "_" + targetDoc.guid || ""
      );
      if (iframe) {
        const msg = new NotifyTargetDocMessage(
          MODULE_TYPE.SHELL,
          sideDoc?.id || "",
          sideDoc?.type || "",
          sideDoc?.name || ""
        );
        iframe.contentWindow?.postMessage(msg, "*");
      } else {
        console.log("notifyTargetDoc: iframe not found: " + targetDoc.type + "_" + targetDoc.guid);
      }
    }
  }

  private findIFrameByGuid(guid: string) {
    const iframes = document.getElementsByTagName("iframe");
    for (let i = 0; i < iframes.length; i++) {
      if (iframes[i].id === guid) {
        return iframes[i];
      }
    }
    return null;
  }


  public getDocFromDocId(docId: string): DocDto | undefined {
    return this.allDocs.find((doc) => doc.id === docId);
  }

  //#region  Filtri doc
  public getDocsFromServer() {
    this._docsLoaded.set(false);
    const result = new Array<DocDto>();
    if (!navigator.onLine) {
      console.error("getDocsFromServer: no connection");
    }
    if (this.authenticationService.isAuthenticated()) {
      this.allDocs = [];
      let recentDoc: DocDto;
      this.firebaseService.getDownloadUri("assets/noimage.png").then((url) => {
        this.noImageUrl = url;
        if (this.getRecentDocsSubscription) {
          this.getRecentDocsSubscription.unsubscribe();
        }
        this.getRecentDocsSubscription = this.apiService.getRecentDocs().subscribe(
          (resp: any) => {
            const viewStringSing = this.translateService.instant("RECENT_VIEW_SING");
            const copyStringSing = this.translateService.instant("RECENT_COPY_SING");
            const viewStringPlur = this.translateService.instant("RECENT_VIEW_PLUR");
            const copyStringPlur = this.translateService.instant("RECENT_COPY_PLUR");
            let num = 0;
            resp.forEach((recent: any) => {
              num++;
              recentDoc = new DocDto(
                num,
                recent,
                viewStringSing,
                copyStringSing,
                viewStringPlur,
                copyStringPlur
              );
              this.updateDocData(recentDoc, recent.shareData);
              const path = `docs/${recent.id}/preview.jpg`;
              this.getDocPreview(recentDoc, path);
              this.allDocs.push(recentDoc);
              this._docsLoaded.set(true);
            });
            //  this.loadLinked();
            //const docsToShow =

            this.filterDocs();
            this.loadedAllDocuments.emit(true);
            // this._docs.update(() => docsToShow);
          },
          (error: any) => {
            console.error("getDocsFromServer().subscribe: " + error);
            this.loadedAllDocuments.emit(false);
          }
        );
      });
    } else {
      this.loadedAllDocuments.emit(false);
      this.router.navigate(["login", "/home"]);
    }
    return result;
  }

  getDocPreview(recent: DocDto, path: string) {
    this.firebaseService.getDownloadUri(path).then((url) => {
      recent.previewPublicUrl = url;
    });
  }
  private updateDocData(doc: DocDto, docData: any) {
    if (docData) {
      doc.isShared = docData.isShared ? true : false;
      doc.isPubliclyShared = docData.isPubliclyShared ? true : false;
      doc.isMine = docData.isMine ? true : false;
      doc.readonly = docData.isReadonly ? true : false;
      doc.shareType = docData.shareType ? docData.shareType : "";
      doc.stat_copyShared = docData.stat_copyShared
        ? docData.stat_copyShared
        : 0;
      doc.stat_viewShared = docData.stat_viewShared
        ? docData.stat_viewShared
        : 0;
      doc.updateStrings();
    } else {
      console.log(`No recent data available`);
    }
  }


  public sortRecentDocs(docs: Array<DocDto>, type: string) {
    let result = new Array<DocDto>();
    const epoch = new Date(0);
    const fixShift = (1000 * 60 * 60 * 24 * 365.25 * 1000);
    if (type === "alphabetical") {
      result = docs.sort((n1, n2) => {
        const nameA: string = (n1.fixDate > epoch ? '0_' : '1_') + n1.name.toLowerCase();
        const nameB: string = (n2.fixDate > epoch ? '0_' : '1_') + n2.name.toLowerCase();
        if (nameA > nameB) {
          return 1;
        }
        if (nameA < nameB) {
          return -1;
        }
        return 0;
      });
    } else {
      result = docs.sort((n1, n2) => {
        const n1Time = (n1.fixDate > epoch ? (n1.fixDate.getTime() + fixShift) : (n1.lastOpened ? n1.lastOpened.getTime() : new Date().getTime()));
        const n2Time = (n2.fixDate > epoch ? (n2.fixDate.getTime() + fixShift) : (n2.lastOpened ? n2.lastOpened.getTime() : new Date().getTime()));
        return n2Time - n1Time;
      });
    }
    return result;
  }



  public filterDocs() {

    const pdfs = new Array<DocDto>();
    const maps = new Array<DocDto>();
    const docs = new Array<DocDto>();
    const docsTags = new Array<DocDto>();
    //const sortedDocs = this.sortByLastOpened(this.allDocs, 'desc');
    const sortedDocs = this.sortRecentDocs(this.allDocs, 'date');

    sortedDocs.forEach((doc: DocDto) => {
      const isDeleted =
        doc.deleteDate !== null &&
        doc.deleteDate !== undefined &&
        doc.deleteDate !== null;
      if (!isDeleted) {
        switch (doc.docType) {
          case "editor": {
            docs.push(doc);
            break;
          }
          case "pdf": {
            pdfs.push(doc);
            break;
          }
          case "map": {
            maps.push(doc);
            break;
          }

        }
        let addDoc = false;
        if (this.signalTags().length > 0) {
          addDoc = false;
          if (doc.tags.length > 0) {
            this.signalTags().forEach(tag => {
              if (doc.tags.includes(tag)) {
                addDoc = true;
              }
            });
          }
        }
        if (addDoc) {
          docsTags.push(doc);
        }
      }

    });
    this._docs.set(docs);
    this._pdfs.set(pdfs);
    this._maps.set(maps);
    this._docsFiltered.set(docsTags);

  }

  closeDoc(docId: string, forceExit: boolean = false, noForceExit: boolean = false): Promise<any> {
    const resDoc = { canClose: true, saving: false };
    if (this.networkStatusLineService.offline()) {
      const msg = this.translateService.instant('GATEWAY_TIMEOUT') + ' ' + this.translateService.instant('UNSAVED_CHANGES');
      this.snackBarService.openSnackBarAndDoAction(msg, this.translateService.instant('OK'), 'alert', 'center', docId, () => { });
      resDoc.canClose = false;
      resDoc.saving = false;
      return Promise.resolve(resDoc);
    }
    // documento impegnato annullo la chiusura
    const doc = this.moduleSpacesService.getOpenDocs()?.find((doc: OpenDocInfoDto | null) => doc?.id === docId);
    return new Promise((resolve) => {
      Promise.resolve().then(() => {
        if (doc?.isInViewMode || forceExit) {
          return Promise.resolve(true)
        } else {
          if (doc?.isBusy || doc?.saveStatus == 1 || doc?.isDirty || this.shellStateService.isClaireWorking) {
            resDoc.canClose = false;
            resDoc.saving = false;
            let msg = this.translateService.instant('WORKSPACE_BUSY')
            if (doc?.saveStatus == 1 || this.shellStateService.isClaireWorking) {
              msg = doc?.saveStatus == 1 ? this.translateService.instant('SAVING') : this.translateService.instant('WAITING_CLAIRE');
              if (noForceExit) {
                this.snackBarService.openSnackBarAndDoAction(msg, this.translateService.instant('STOP_ANYWAY'), 'alert', 'center', docId, () => { });
              } else {
                this.snackBarService.openSnackBarSimple(msg, this.translateService.instant('STOP_ANYWAY'), 'alert', 'center', () => { this.forceExit(docId); }, () => { });
              }
            } else {
              if (doc?.isDirty) {
                msg = this.translateService.instant('DIRTY');
              }
              if (doc?.isNewDoc && !doc?.hasChanged) {
                forceExit = true;
                return Promise.resolve(true);
              } else {
                this.snackBarService.openSnackBarAndDoAction(msg, this.translateService.instant('OK'), 'alert', 'center', docId, () => { });
              }
            }
            return Promise.resolve(false);
          } else {
            return Promise.resolve(true);
          }
        }
      }).then((result) => {
        resDoc.canClose = result;
        if (resDoc.canClose) {
          if (doc !== null && doc !== undefined) {
            if (doc?.isNewDoc && doc?.hasChanged) {
              return Promise.resolve(doc?.name);
              // const title = (doc?.type === MODULE_TYPE.EDITOR) ? this.translateService.instant('NEW_DOC') : this.translateService.instant('NEW_MAP');
              // return this.renameDocService.renameDoc(doc.id, doc.name, this.shellStateService.getPreviewPath(doc.id), doc.type, title);
            } else {

              return Promise.resolve(doc?.name);
            }
          } else {
            return Promise.resolve('');
          }
        } else {
          return Promise.resolve('');
        }
      }).then((res) => {
        if (forceExit) {
          resDoc.canClose = true;
          resDoc.saving = false;
          this.canCloseDoc.emit(doc?.id);
        } else {
          resDoc.canClose = (res != '');
          resDoc.saving = (doc && doc?.saveStatus >= 2 && !doc?.isInViewMode) || doc?.isDirty || false;
          if (resDoc.saving && resDoc.canClose) {
            if (doc !== null && doc !== undefined) {
              this.sendSaveMessage(doc?.id);
              resDoc.canClose = false;
            } else {
              resDoc.saving = false;
            }
          } else {
            resDoc.saving = false;
          }
          if (resDoc.canClose) {
            this.canCloseDoc.emit(doc?.id);
          }
        }
        return resolve(resDoc);
      }).catch((error) => {
        resDoc.canClose = false;
        resDoc.saving = false;
        return resolve(resDoc);
      });
    });
  }

  forceExit(docId: string) {
    console.log('forceExit ' + docId);
    if (docId) {
      this.closeDoc(docId, true);
    } else {
      this.moduleSpacesService.closeAllDocs();
    }
  }

  sendSaveMessage(docId: string): boolean {
    let result = false;
    const msg = new SaveDocRequestMessage(MODULE_TYPE.SHELL, docId);
    result = true;
    const iframes = document.getElementsByTagName("iframe");
    for (let i = 0; i < iframes.length; i++) {
      iframes[i].contentWindow?.postMessage(msg, "*");
    }
    return result;
  }

  closeAllDocs(): Promise<any> {
    const resDoc = { canClose: true, saving: false };
    return new Promise<any>((resolve) => {
      Promise.resolve().then(() => {
        const promises = [];
        for (const od of this.moduleSpacesService.getOpenDocsOrdered()) {
          if (od !== null && od !== undefined) {
            const promise = this.closeDoc(od.id);
            promises.push(promise);
          }
        }
        return Promise.all(promises);
      }).then((values) => {
        resDoc.canClose = values.every((value) => value.canClose === true);
        resDoc.saving = values.every((value) => value.saving === false);
        return resolve(resDoc);
      });
    });
  }

  createDocFile(docId: string, html: any, filename: string, docFileId: string, message: string, action: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.loaderService.startLoading('create_doc', 'PLEASE_WAIT');
      const blob = new Blob([html], { type: 'text/plain' });
      // Convert html to sfdt format
      this.wordProcessorBackendService.loadHtml(blob, filename).subscribe({
        next: async (sfdt: any) => {
          if (sfdt) {
            if (!docFileId) {
              docFileId = Guid.create().toString();
            }
            // Upload document to storage
            const res = await this.firebaseService.uploadStringPromise(sfdt, `docs/${docFileId}/document.sfdt`, 'text/plain');
            // Add document metadata (global DocumentsDictionary and user Documents)
            this.apiService.addDocMetadata(docFileId, filename, 'editor', this.ttsService.selectedLanguage()).subscribe({
              next: () => {
                this.apiService.insertDocFileId(docId, docFileId).subscribe({
                  next: () => {
                    this.loaderService.stopLoading('create_doc');
                    if (this.isDocIdOpen(docFileId)) {
                      action = '';
                      this.shellEmittersService.onReloadDocument.emit(docFileId);
                    }
                    this.snackBarService.openSnackBar(message, action, '', 'center',
                      () => {
                        if (action !== '') {
                          console.log('OPEN DOC: ' + docFileId);
                          this.openDocument.emit(docFileId);
                        }
                      },
                      () => { }
                    );
                    return resolve(docFileId);
                  },
                  error: (error: any) => {
                    this.loaderService.stopLoading('create_doc');
                    console.error(`ERROR createDocFile: ${error}`);
                    return resolve('');
                  }
                });
              },
              error: (error: any) => {
                this.loaderService.stopLoading('create_doc');
                console.error(`ERROR createDocFile addDocMetadata: ${error}`);
              }
            });
          } else {
            this.loaderService.stopLoading('create_doc');
            return reject('No sfdt file created');
          }
        },
        error: (error: any) => {
          this.loaderService.stopLoading('create_doc');
          console.error(`ERROR createDocFile: ${error}`);
          return reject(error);
        }
      });
    });
  }

  isDocIdOpen(docId: string): boolean {
    const doc = this.moduleSpacesService.getOpenDocs().find((doc: OpenDocInfoDto | null) => doc?.id === docId);
    if (doc) {
      return true;
    } else {
      return false;
    }
  }

  public addSideBySide() {
    const doc1 = this.moduleSpacesService.getOpenDocs()[0];
    const doc2 = this.moduleSpacesService.getOpenDocs()[1];
    if (doc1 && doc2) {

      this.addSideBySideSubscription?.unsubscribe();
      this.addSideBySideSubscription = this.apiService.addSidebySideDocs(doc1.id, doc2.id).subscribe({
        next: () => {
          console.log("added linked doc");
          this.getDocsFromServer();
        }, error: ((err: any) => {
          console.log(err);
        })
      });
    }
  }

  getOpenDocsOrdered(): Array<OpenDocInfoDto | null> {
    return this.moduleSpacesService.getOpenDocsOrdered();
  }

  getOpenDocs(): Array<OpenDocInfoDto | null> {
    return this.moduleSpacesService.getOpenDocs();
  }

  getFocusedDoc(): OpenDocInfoDto | null {
    return this.moduleSpacesService.getOpenDocs()[this.focusedSpace];
  }

  getUnfocusedDoc(): OpenDocInfoDto | null {
    return this.moduleSpacesService.getOpenDocs()[(this.focusedSpace === 0 ? 1 : 0)];
  }

  atLeastOneEditableDoc(): boolean {
    return this.moduleSpacesService.getOpenDocsOrdered().findIndex(d => !d?.isInViewMode) != -1;
  }
  atLeastOneDocOfType(docType: string): boolean {
    return this.moduleSpacesService.getOpenDocsOrdered().findIndex(d => d?.type == docType) != -1;
  }

  focusedDocOfTypeAndOtherEditableDoc(docType: string): boolean {
    let res = false;
    const docs = this.moduleSpacesService.getOpenDocsOrdered().filter(d => !d?.isInViewMode);
    if (docs.length > 0) {
      const focused = this.getFocusedDoc();
      if (focused) {
        res = focused.type === docType;
      }
      if (res) {
        res = this.moduleSpacesService.getOpenDocsOrdered().findIndex(d => !d?.isInViewMode) != -1;
      }
    }
    return res;

  }

  focusedDocAndOtherEditableDoc(): boolean {
    let res = false;
    const docs = this.moduleSpacesService.getOpenDocsOrdered().filter(d => !d?.isInViewMode);
    if (docs.length > 0) {
      const focused = this.getFocusedDoc();
      if (focused) {

        res = this.moduleSpacesService.getOpenDocsOrdered().findIndex(d => !d?.isInViewMode && this.authenticationService.isModuleEnabled(d?.type)) != -1;
      }
    }
    return res;

  }

  atLeastOneEditableDocOfType(docType: string): boolean {
    return this.moduleSpacesService.getOpenDocsOrdered().findIndex(d => !d?.isInViewMode && d?.type == docType) != -1;
  }

  getEditableDocs(): (OpenDocInfoDto | null)[] {
    let res: (OpenDocInfoDto | null)[] = [];
    if (this.moduleSpacesService.getOpenDocsOrdered() != null) {
      res = this.moduleSpacesService.getOpenDocsOrdered().filter(d => !d?.isInViewMode);
    }
    return res;
  }

  getAllEnabledDocs(): (OpenDocInfoDto | null)[] {
    let res: (OpenDocInfoDto | null)[] = [];
    if (this.moduleSpacesService.getOpenDocsOrdered() != null) {
      res = this.moduleSpacesService.getOpenDocsOrdered().filter(d => this.authenticationService.isModuleEnabled(d?.type));
    }
    return res;
  }

  setOpenAfterClose(doc: OpenDocInfoDto, space: number) {
    this.OpeningDocInfo = {
      docInfo: doc,
      space: space
    };
  }

  public showNoMathEnabledMessage() {
    this.dialog.open(DialogAdvancedFunctionComponent);
  }

}